import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { FaArrowUp } from "react-icons/fa";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 40px;
  right: 40px;
  z-index: 999;
  width: 54px;
  height: 54px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
  transform: ${({ isVisible }) => (isVisible ? "scale(1)" : "scale(0.5)")};
  pointer-events: ${({ isVisible }) => (isVisible ? "all" : "none")};
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    bottom: 24px;
    right: 24px;
    width: 48px;
    height: 48px;
  }
`;

const ProgressSVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: transform 0.3s ease;

  ${({ isScrolling }) =>
    isScrolling &&
    css`
      animation: ${spin} 1s linear infinite;
    `}
`;

const ProgressCircle = styled.circle`
  fill: none;
  stroke: ${({ theme }) => theme.primary || "#854CE6"};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 157; /* 2 * Math.PI * 25 */
  stroke-dashoffset: ${({ progress }) => 157 - (progress / 100) * 157};
  transition: stroke-dashoffset 0.1s ease;
`;

const TrackCircle = styled.circle`
  fill: none;
  stroke: rgba(255, 255, 255, 0.15);
  stroke-width: 3;
`;

const ButtonInner = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ theme }) => theme.card || "#171721"};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.text_primary || "#F2F3F4"};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  z-index: 2;
  transition: all 0.3s ease;

  svg {
    font-size: 16px;
    transition: transform 0.3s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.primary || "#854CE6"};
    color: white;
    box-shadow: 0 0 15px rgba(133, 76, 230, 0.5);

    svg {
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    svg {
      font-size: 14px;
    }
  }
`;

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    // Toggle visibility based on 400px threshold
    if (scrollTop > 400) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    // Calculate percentage progress
    if (docHeight > 0) {
      const progressPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(progressPercent);
    }
  };

  const scrollToTop = () => {
    setIsScrolling(true);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Reset spinning animation once page hits top
    const checkScrollTop = setInterval(() => {
      if (window.pageYOffset === 0) {
        setIsScrolling(false);
        clearInterval(checkScrollTop);
      }
    }, 100);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <ButtonWrapper isVisible={isVisible} onClick={scrollToTop}>
      <ProgressSVG isScrolling={isScrolling} viewBox="0 0 54 54">
        <TrackCircle cx="27" cy="27" r="25" />
        <ProgressCircle cx="27" cy="27" r="25" progress={scrollProgress} />
      </ProgressSVG>
      <ButtonInner>
        <FaArrowUp />
      </ButtonInner>
    </ButtonWrapper>
  );
};

export default ScrollToTop;
